// src/components/AGTFit/goals/Calories.js
import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  Select, 
  MenuItem,
  CircularProgress,
  useTheme,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Nuevo NavigationBar estilizado con ancho adaptable
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '350px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : 'none',
}));

// TabIndicator modificado para usar transform
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
}));

// StyledTab con ancho adaptable al contenido
const StyledTab = styled(Box)(({ theme, selected }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: 'auto',
  padding: '0 16px',
  borderRadius: '24px',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...(selected && {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }),
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
  },
}));

// Gráfico de barras con altura aumentada
const BarGraph = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 250, // Aumentada para dar más espacio a las barras
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 32, 0.8)' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  borderRadius: 16,
  padding: theme.spacing(2, 2, 4, 2),
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  position: 'relative',
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.05)'),
}));

const Calories = ({ caloriesData, loading, goalData, onPeriodChange }) => {
  const theme = useTheme();
  const [caloriesFilter, setCaloriesFilter] = useState('Calories');
  const [activeCaloriesTimeTab, setActiveCaloriesTimeTab] = useState(0);
  const [weekAverage, setWeekAverage] = useState(0);
  
  // Referencias para los tabs
  const caloriesTabsContainerRef = useRef(null);
  const caloriesTabRefs = useRef([]);
  if (caloriesTabRefs.current.length === 0) {
    caloriesTabRefs.current = Array(6).fill(null).map(() => ({ current: null }));
  }
  
  // Estado para el indicador de tabs
  const [caloriesIndicatorStyle, setCaloriesIndicatorStyle] = useState({
    left: '6px',
    width: '74px'
  });

  // Opciones para los selectores
  const caloriesFilterOptions = [
    'Calories', 'Protein', 'Carbohydrates', 'Total Fat', 
    'Saturated Fat', 'Trans Fat', 'Fiber', 'Sugars', 
    'Sodium', 'Salt', 'Water'
  ];
  
  // Opciones para los tabs de tiempo
  const timeTabOptions = ['Week', '7D', '1M', '3M', '1Y', 'MAX'];

  // Log de datos cuando cambian
  useEffect(() => {
  }, [caloriesData]);

  // Actualizar la posición del indicador de calorías cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (caloriesTabRefs.current[activeCaloriesTimeTab]?.current) {
        const tabElement = caloriesTabRefs.current[activeCaloriesTimeTab].current;
        const tabsContainer = caloriesTabsContainerRef.current;
        
        if (tabElement && tabsContainer) {
          // Obtener la posición del contenedor de pestañas
          const containerRect = tabsContainer.getBoundingClientRect();
          // Obtener la posición de la pestaña activa
          const tabRect = tabElement.getBoundingClientRect();
          
          // Calcular la posición relativa de la pestaña dentro del contenedor
          const left = tabRect.left - containerRect.left;
          
          setCaloriesIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };
    
    // Pequeño retraso para asegurar que los elementos estén renderizados
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeCaloriesTimeTab, caloriesTabRefs]);

  // Calcular el promedio semanal basado en los datos y el filtro actual
  useEffect(() => {
    // Verificar si hay datos para calcular el promedio
    if (caloriesData && caloriesData.length > 0) {
      // Mapeo de nombres de filtros a nombres de campos en los datos
      const fieldMap = {
        'Calories': 'calories',
        'Protein': 'protein',
        'Carbohydrates': 'carbs',
        'Total Fat': 'fats',
        'Fiber': 'fiber',
        'Sugars': 'sugar',
        'Sodium': 'sodium',
        'Salt': 'salt',
        'Water': 'water',
        'Saturated Fat': 'saturatedFat',
        'Trans Fat': 'transFat'
      };
      
      // Obtener el nombre del campo según el filtro seleccionado
      const field = fieldMap[caloriesFilter] || 'calories';
      
      // Calcular la suma de los valores para el campo seleccionado
      let sum = 0;
      let count = 0;
      
      for (const item of caloriesData) {
        if (item[field] !== undefined && item[field] !== null) {
          // Convertir a número y sumar
          const value = parseFloat(item[field]);
          if (!isNaN(value)) {
            sum += value;
            count++;
          }
        }
      }
      
      // Calcular el promedio si hay datos válidos
      const avg = count > 0 ? Math.round(sum / count) : 0;
      setWeekAverage(avg);
    } else {
      setWeekAverage(0);
    }
  }, [caloriesData, caloriesFilter]);

  // Manejadores para los cambios de pestaña
  const handleCaloriesTimeTabChange = (newValue) => {
    setActiveCaloriesTimeTab(newValue);
    // Cargar datos según el período de tiempo
    const periods = ['week', 'week', 'month', '3month', 'year', 'max'][newValue];
    onPeriodChange(periods);
  };

  // Renderizar el gráfico de barras para calorías
  const renderCalorieGraph = (data) => {
    
    // Verificar si hay datos reales
    if (!data || data.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body1" color="text.secondary">No data available</Typography>
        </Box>
      );
    }
    
    // Mapeo de nombres de filtros a nombres de campos en los datos
    const fieldMap = {
      'Calories': 'calories',
      'Protein': 'protein',
      'Carbohydrates': 'carbs',
      'Total Fat': 'fats',
      'Fiber': 'fiber',
      'Sugars': 'sugar',
      'Sodium': 'sodium',
      'Salt': 'salt',
      'Water': 'water',
      'Saturated Fat': 'saturatedFat',
      'Trans Fat': 'transFat'
    };
    
    // Obtener el nombre del campo según el filtro seleccionado
    const field = fieldMap[caloriesFilter] || 'calories';
    
    // Determinar si hay datos reales para mostrar
    const hasRealData = data.some(item => {
      const value = parseFloat(item[field]);
      return !isNaN(value) && value > 0;
    });
    
    // Calcular un valor máximo razonable para las barras
    let maxValue = 0;
    if (hasRealData) {
      // Usar el objetivo como referencia si existe, de lo contrario usar el máximo de los datos
      if (goalData?.goalCalories && field === 'calories') {
        // Establecer el máximo como 1.25 veces el objetivo o el valor máximo de datos, lo que sea mayor
        const dataMax = Math.max(...data.map(item => {
          const value = parseFloat(item[field]);
          return !isNaN(value) ? value : 0;
        }));
        maxValue = Math.max(dataMax, goalData.goalCalories * 1.25);
      } else {
        maxValue = Math.max(...data.map(item => {
          const value = parseFloat(item[field]);
          return !isNaN(value) ? value : 0;
        }));
        // Agregar un 25% adicional para evitar que las barras toquen el límite
        maxValue *= 1.25;
      }
    }
    
    // Altura máxima de la barra en píxeles
    const maxBarHeight = 180; // Altura máxima en píxeles
    
    // Definir la animación de crecimiento proporcional
    const growKeyframes = `
      @keyframes barGrow {
        from {
          height: 0;
        }
        to {
          height: var(--final-height);
        }
      }
    `;
    
    return (
      <BarGraph>
        {/* Estilo para la animación */}
        <style>{growKeyframes}</style>
        
        {/* Línea del objetivo - Corregida */}
        {goalData?.goalCalories && caloriesFilter === 'Calories' && (
          <>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: '1px',
                borderTop: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'}`,
                // Usar la misma lógica que se usa para calcular la altura de las barras
                top: `${maxValue > 0 ? `calc(100% - ${(goalData.goalCalories / maxValue) * maxBarHeight}px - 40px)` : '25%'}`,
                display: 'block',
                zIndex: 1,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                right: 8,
                top: `${maxValue > 0 ? `calc(100% - ${(goalData.goalCalories / maxValue) * maxBarHeight}px - 55px)` : 'calc(25% - 15px)'}`,
                color: theme.palette.text.secondary,
                fontSize: '10px',
                display: 'block',
              }}
            >
              Goal: {goalData.goalCalories} {getUnit()}
            </Typography>
          </>
        )}
        
        {data.map((bar, index) => {
          // Obtener el valor numérico para este punto de datos
          const rawValue = bar[field];
          const value = parseFloat(rawValue);
          const displayValue = !isNaN(value) ? value : 0;
          
          // Calcular altura de la barra en píxeles
          const heightPixels = maxValue > 0 
            ? Math.max(Math.round((displayValue / maxValue) * maxBarHeight), displayValue > 0 ? 15 : 0) 
            : 0;
          
          return (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                width: `${100 / data.length}%`,
                position: 'relative'
              }}
            >
              {/* Valor numérico sobre la barra */}
              {displayValue > 0 && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'absolute',
                    bottom: `calc(${heightPixels}px + 10px)`,
                    fontWeight: 600,
                    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textAlign: 'center'
                  }}
                >
                  {Math.round(displayValue)}
                </Typography>
              )}
              
              {/* Barra con Tooltip - Animación corregida */}
              <Tooltip 
                title={displayValue > 0 ? `${Math.round(displayValue)} ${getUnit()}` : 'No data'} 
                placement="top"
                arrow
              >
                <Box 
                  sx={{ 
                    width: '8px',
                    height: `${heightPixels}px`,
                    background: displayValue > 0 
                      ? (bar.active 
                          ? 'linear-gradient(to top, #FF6B00, #FF9800)' 
                          : theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.3)' 
                            : 'rgba(0,0,0,0.15)')
                      : theme.palette.mode === 'dark'
                        ? 'rgba(100,100,100,0.4)' 
                        : 'rgba(200,200,200,0.6)',
                    borderRadius: '4px',
                    transition: 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                    '--final-height': `${heightPixels}px`, // Variable CSS para la altura final
                    animation: displayValue > 0 ? 'barGrow 1s ease forwards' : 'none',
                    '&:hover': {
                      background: displayValue > 0
                        ? (bar.active 
                            ? 'linear-gradient(to top, #FF6B00, #FF9800)' 
                            : theme.palette.mode === 'dark' 
                              ? 'rgba(255,255,255,0.4)' 
                              : 'rgba(0,0,0,0.2)')
                        : theme.palette.mode === 'dark'
                          ? 'rgba(120,120,120,0.5)' 
                          : 'rgba(180,180,180,0.7)',
                      transform: 'scaleX(1.2)',
                    }
                  }} 
                />
              </Tooltip>
              
              {/* Etiqueta del día */}
              <Typography 
                variant="caption" 
                sx={{ 
                  position: 'absolute',
                  bottom: -24,
                  color: bar.active ? FIT_ORANGE : (displayValue > 0 ? theme.palette.text.primary : theme.palette.text.secondary),
                  fontWeight: bar.active ? 600 : (displayValue > 0 ? 500 : 400),
                  opacity: displayValue > 0 ? 1 : 0.7,
                }}
              >
                {bar.dayLabel || bar.label}
              </Typography>
            </Box>
          );
        })}
      </BarGraph>
    );
  };

  // Obtener la unidad de medida según el filtro seleccionado
  const getUnit = () => {
    switch(caloriesFilter) {
      case 'Calories':
        return 'kcal';
      case 'Protein':
      case 'Carbohydrates':
      case 'Total Fat':
      case 'Saturated Fat':
      case 'Trans Fat':
      case 'Fiber':
      case 'Sugars':
        return 'g';
      case 'Sodium':
      case 'Salt':
        return 'mg';
      case 'Water':
        return 'ml';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Calories
        </Typography>
        
        <FormControl variant="standard" size="small" sx={{ minWidth: 120 }}>
          <Select
            value={caloriesFilter}
            onChange={(e) => setCaloriesFilter(e.target.value)}
            disableUnderline
            sx={{ 
              fontSize: '0.875rem',
              '& .MuiSelect-select': {
                paddingBottom: 0
              }
            }}
            IconComponent={ChevronDown}
          >
            {caloriesFilterOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Goal y Week Average */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Goal
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {goalData?.goalCalories ? `${goalData.goalCalories} ${getUnit()}` : 'Set a goal'}
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary">
            Week Average
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {`${weekAverage} ${getUnit()}`}
          </Typography>
        </Box>
      </Box>
      
      {/* Navigation Bar para el tiempo */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 4 }}>
        <StyledTabs ref={caloriesTabsContainerRef}>
          <TabIndicator style={caloriesIndicatorStyle} />
          {timeTabOptions.map((tab, index) => (
            <StyledTab 
              key={tab}
              ref={caloriesTabRefs[index]}
              onClick={() => handleCaloriesTimeTabChange(index)}
              selected={activeCaloriesTimeTab === index}
            >
              {tab}
            </StyledTab>
          ))}
        </StyledTabs>
      </Box>
      
      {/* Gráfico de calorías */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
        </Box>
      ) : (
        renderCalorieGraph(caloriesData)
      )}
    </Box>
  );
};

export default Calories;